/**
 * Arborescence UNIQUE de l'application interne, filtrée par droits (décision du
 * 17/10/2026). Elle remplace les deux arbres recopiés à la main (terrain et
 * bureau) qui divergeaient : un lien valable au bureau menait le terrain à
 * l'accueil. Le poste ne décide plus de l'existence d'une page, seulement de
 * la navigation proposée (`app/areas.ts`).
 */
import type { ReactNode } from 'react';
import { Route } from 'react-router-dom';
import { AccessDenied } from '../components/ui/Feedback.js';
import { routeAllowed, type AppRoute, type RouteAccess } from './route-types.js';
import { homeRoutes } from '../features/home/routes.js';
import { planningRoutes } from '../features/planning/routes.js';
import { tasksRoutes } from '../features/tasks/routes.js';
import { messagesRoutes } from '../features/messages/routes.js';
import { requestsRoutes } from '../features/requests/routes.js';
import { feedbackRoutes } from '../features/feedback/routes.js';
import { interventionsRoutes } from '../features/interventions/routes.js';
import { emergenciesRoutes } from '../features/emergencies/routes.js';
import { customersRoutes } from '../features/customers/routes.js';
import { equipmentRoutes } from '../features/equipment/routes.js';
import { stockRoutes } from '../features/stock/routes.js';
import { tradesRoutes } from '../features/trades/routes.js';
import { fluidsRoutes } from '../features/fluids/routes.js';
import { contractsRoutes } from '../features/contracts/routes.js';
import { quotesRoutes } from '../features/quotes/routes.js';
import { documentsRoutes } from '../features/documents/routes.js';
import { invoicesRoutes } from '../features/invoices/routes.js';
import { suppliersRoutes } from '../features/suppliers/routes.js';
import { timeRoutes } from '../features/time/routes.js';
import { absencesRoutes } from '../features/absences/routes.js';
import { teamRoutes } from '../features/team/routes.js';
import { analyticsRoutes } from '../features/analytics/routes.js';
import { syncRoutes } from '../features/sync/routes.js';
import { notificationsRoutes } from '../features/notifications/routes.js';
import { profileRoutes } from '../features/profile/routes.js';
import { companyDocumentsRoutes } from '../features/company-documents/routes.js';
import { helpRoutes } from '../features/help/routes.js';
import { importRoutes } from '../features/import/routes.js';
import { settingsRoutes } from '../features/settings/routes.js';
import { subcontractorOfficeRoutes } from '../features/subcontractor/office/routes.js';

export const APP_ROUTES: readonly AppRoute[] = [
  ...homeRoutes,
  ...planningRoutes,
  ...tasksRoutes,
  ...messagesRoutes,
  ...requestsRoutes,
  ...feedbackRoutes,
  ...interventionsRoutes,
  ...emergenciesRoutes,
  ...customersRoutes,
  ...equipmentRoutes,
  ...stockRoutes,
  ...tradesRoutes,
  ...fluidsRoutes,
  ...contractsRoutes,
  ...quotesRoutes,
  ...documentsRoutes,
  ...invoicesRoutes,
  ...suppliersRoutes,
  ...timeRoutes,
  ...absencesRoutes,
  ...teamRoutes,
  ...analyticsRoutes,
  ...syncRoutes,
  ...notificationsRoutes,
  ...profileRoutes,
  ...companyDocumentsRoutes,
  ...helpRoutes,
  ...importRoutes,
  ...settingsRoutes,
  ...subcontractorOfficeRoutes,
];

/**
 * Les routes du compte, à poser sous la coque. Une adresse refusée garde sa
 * place et montre l'état « Accès refusé » (retour à l'accueil) : omise, elle laisserait une
 * route voisine la capter (`/factures/nouvelle` lue comme la facture
 * « nouvelle »).
 */
export function appRouteElements(access: RouteAccess): ReactNode[] {
  return APP_ROUTES.map((route) => (
    <Route
      key={route.path}
      path={route.path}
      element={routeAllowed(route.gate, access)
        ? route.element
        : <AccessDenied />}
    />
  ));
}
